import { ApiResponse } from "../../interfaces/genericResponse/GenericResponse";
import { AllEvent, AllEventTeam, AllTeams, AllUsers, DenyMeetingInvitationReq, EventData, GetTeamDto, TimeOffEmpData, userTask } from "../../interfaces/identity/calendar/Events";
import Team from "../../interfaces/identity/Team";


export async function getAllEvents(
    authToken: string,
    start: Date,
    end: Date
): Promise<AllEvent[] | null> {
    const startTime = start.toISOString();
    const endTime = end.toISOString();
    const apiUrl: string = `${process.env.REACT_APP_API_BASE_URL}/CalendarEvent/GetAllEvent?startTime=${startTime}&endTime=${endTime}`;

    try {
        const response = await fetch(apiUrl, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
        });

        if (!response.ok) {
            console.error("Failed to fetch events:", response.statusText);
            return null;
        }

        const json = await response.json();
        const data: AllEvent[] = json.data ?? [];

        // Filter only December events (month 9)
        const decemberEvents = json.data.filter((ev: any) => {
            const start = new Date(ev.startTime);
            return start.getMonth() === 11;
        });

        return data;
    } catch (err) {
        console.error("Error fetching roles:", err);
        return null;
    }
}


export async function getAllEventsWithFilter(
    authToken: string
): Promise<AllEvent[] | null> {
    const apiUrl: string = `${process.env.REACT_APP_API_BASE_URL}/CalendarEvent/GetAllEvent`;

    try {
        const response = await fetch(apiUrl, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
        });

        if (!response.ok) {
            console.error("Failed to fetch events:", response.statusText);
            return null;
        }

        const json = await response.json();
        const data: AllEvent[] = json.data ?? [];

        return data;
    } catch (err) {
        console.error("Error fetching roles:", err);
        return null;
    }
}

export async function getAllEmployees(
    authToken: string
): Promise<AllUsers[] | null> {
    const apiUrl: string = `${process.env.REACT_APP_API_BASE_URL}/Authentication/get-combined-user-list`;

    try {
        const response = await fetch(apiUrl, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
        });


        if (!response.ok) {
            console.error("Failed to fetch events:", response.statusText);
            return null;
        }

        const json = await response.json();
        const data: AllUsers[] = json.data ?? [];

        return data;
    } catch (err) {
        console.error("Error fetching roles:", err);
        return null;
    }
}

export async function getAllTeam(
    authToken: string,
    requiredMembers: boolean = true
): Promise<ApiResponse<AllTeams[]>> {
    const apiUrl: string = `${process.env.REACT_APP_API_BASE_URL}/TeamandRegion/GetTeamDetails?requiredMembers=${requiredMembers}`;

    try {
        const response = await fetch(apiUrl, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
        });

        const json = await response.json();

        return json;
    } catch (err) {
        throw err;
    }
}

export async function getMyTeams(
    authToken: string,
): Promise<Team[]> {
    const apiUrl: string = `${process.env.REACT_APP_API_BASE_URL}/users/teams`;

    try {
        const response = await fetch(apiUrl, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
        });

        const json = await response.json();

        return json;
    } catch (err) {
        throw err;
    }
}

export async function getMyParticipantsAndCoworkers(
    authToken: string,
): Promise<AllUsers[]> {
    const apiUrl: string = `${process.env.REACT_APP_API_BASE_URL}/users/participantAndCoworkers`;

    try {
        const response = await fetch(apiUrl, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
        });

        const json = await response.json();

        const allusers = json.map((user: any) => {
            return {
                userId: user.id,
                userName: user.name,
                email: user.email,
                type: user.type,
                roles: Array.isArray(user.roles) ? user.roles : [],
            };
        })

        return allusers;
    } catch (err) {
        throw err;
    }
}

export async function getAllTeamByRegions(
    authToken: string,
    regionIds: number[]
): Promise<GetTeamDto[]> {
    const query = regionIds.map(id => `regionIds=${id}`).join("&");
    const apiUrl: string = `${process.env.REACT_APP_API_BASE_URL}/TeamandRegion/GetTeamsByRegions?${query}`;

    try {
        const response = await fetch(apiUrl, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
        });

        const json = await response.json();

        return json;
    } catch (err) {
        throw err;
    }
}

export async function getAllTemaEvents(
    authToken: string,
    teamId: number
): Promise<AllEventTeam | null> {
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/CalendarEvent/manager/team/calendar?teamId=${teamId}`;

    try {
        const response = await fetch(apiUrl, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
        });

        if (!response.ok) {
            console.error("Failed to fetch team events:", response.status, response.statusText);
            return null;
        }

        const json = await response.json();

        return json.data ?? null;
    } catch (err) {
        console.error("Error fetching team events:", err);
        return null;
    }
}

export async function createEvent(
    eventData: EventData,
    authToken: string
): Promise<any | null> {
    const apiUrl: string = `${process.env.REACT_APP_API_BASE_URL}/CalendarEvent/CreateEvent`;

    try {
        const response = await fetch(apiUrl, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify(eventData),
        });

        if (response.status === 409) {
            const errorJson = await response.json();
            return { success: false, statusCode: 409, message: errorJson.message || "Conflict: Event overlaps with an existing event." };
        }

        const json = await response.json();
        return json;
    } catch (err) {
        console.error("Error creating event:", err);
        return null;
    }
}

export async function createWeeklySupervisionMeeting(
    instance: number | undefined,
    eventData: EventData,
    authToken: string
): Promise<any | null> {
    const apiUrl: string = `${process.env.REACT_APP_API_BASE_URL}/reporting/weekly-supervision/weekly-supervision/${instance}/schedule-meeting`;

    try {
        const response = await fetch(apiUrl, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify(eventData),
        });

        // if (!response.ok) {
        //   const error = await response.text();
        //   console.error("Failed to create event:", error);
        //   return null;
        // }

        const json = await response.json();
        return json;
    } catch (err) {
        console.error("Error creating event:", err);
        return null;
    }
}

export async function updateEvent(
    eventData: EventData & { id: string },
    authToken: string,
    updateScope?: 'single' | 'future' // Add updateScope parameter
): Promise<any | null> {
    let apiUrl: string = `${process.env.REACT_APP_API_BASE_URL}/CalendarEvent/UpdateEventById/${eventData.id}`;

    if (updateScope) {
        apiUrl += `?updateScope=${updateScope}`;
    }

    try {
        const response = await fetch(apiUrl, {
            method: "PUT",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify(eventData),
        });

        if (response.status === 409) {
            const errorJson = await response.json();
            return { success: false, statusCode: 409, message: errorJson.message || "Conflict: Event overlaps with an existing event." };
        }

        const json = await response.json();
        return json;
    } catch (err) {
        console.error("Error updating event:", err);
        return null;
    }
}

export async function deleteEvent(eventId: string, authToken: string, deleteScope?: 'single' | 'future'): Promise<boolean> {
    let apiUrl = `${process.env.REACT_APP_API_BASE_URL}/CalendarEvent/DeleteEvent/${eventId}`;

    if (deleteScope) {
        apiUrl += `?deleteScope=${deleteScope}`;
    }

    try {
        const response = await fetch(apiUrl, {
            method: "DELETE",
            headers: {
                Authorization: `Bearer ${authToken}`,
            },
        });

        if (!response.ok) {
            const error = await response.text();
            console.error("Failed to delete event:", error);
            return false;
        }

        return true;
    } catch (err) {
        console.error("Error deleting event:", err);
        return false;
    }
}

export async function getAllUserTask(
    authToken: string
): Promise<userTask[] | null> {
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/CalendarEvent/userassignedtask`;

    try {
        const response = await fetch(apiUrl, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
        });

        if (!response.ok) {
            console.error("Failed to fetch tasks:", response.statusText);
            return null;
        }

        const json = await response.json();

        if (!json.success || !Array.isArray(json.data)) {
            console.warn("Unexpected API response format:", json);
            return null;
        }

        return json.data as userTask[];
    } catch (err) {
        console.error("Error fetching tasks:", err);
        return null;
    }
}

export async function getAllLeaves(
    UserId: string,
    authToken: string
): Promise<TimeOffEmpData[] | null> {
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/CalendarEvent/GetAllLeaves?id=${UserId}`;

    try {
        const response = await fetch(apiUrl, {
            method: "Get",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
        })

        if (!response.ok) {
            console.error("Failed to get the leave data:", response.statusText)
        }
        const json = await response.json();

        return json.data ?? null;
    } catch (err) {
        console.error("Service level:", err)
        return null;
    }
}

export async function getEventDatesByUserIdAsync(
    UserId: string,
    authToken: string
): Promise<ApiResponse<string[]>> {
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/CalendarEvent/upcomming/${UserId}/getCalendarEventDates`;

    try {
        const response = await fetch(apiUrl, {
            method: "Get",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
        })

        return await response.json();
    } catch (err) {
        throw err;
    }
}

export async function AcceptRejectMeetingInvitationAsync(
    UserId: string,
    calendarEventId: string,
    isInvitationAccept: boolean,
    authToken: string,
): Promise<ApiResponse<boolean>> {
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/CalendarEvent/invitation/AcceptDenyMeeting`;

    const payload: DenyMeetingInvitationReq = {
        userId: UserId,
        calendarEventId: calendarEventId,
        isInvitationAccept: isInvitationAccept
    }

    try {
        const response = await fetch(apiUrl, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
        })

        return await response.json();
    } catch (err) {
        throw err;
    }
}

